import { Image } from '@sunwebgroup/uds-ui/image'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useId } from 'react'
import { useCMSPageField } from '@/cms/fields'
import { buildImgSrc, buildImgSrcSet, ImageOrigin } from '@/helpers/image-url'
import { useIntl } from '@/intl'
import { getSnowSeason, isResortOpen } from '@/scopes/content/domain/snowInfoHeader'
import styles from './SnowInfoHeader.module.css'

const HERO_IMAGE_WIDTH = 1440
const HERO_IMAGE_HEIGHT = 400
const HERO_IMAGE_WIDTHS = [480, 960, HERO_IMAGE_WIDTH]

export function SnowInfoHeader() {
  const intl = useIntl()
  const titleId = useId()
  const snowInfoHeader = useCMSPageField('ContentPage.snowInfoHeader')
  const destinationName = snowInfoHeader?.destinationName?.trim()

  if (!snowInfoHeader || !destinationName)
    return null

  const {
    imageUrl,
    minAltitudeMeters,
    maxAltitudeMeters,
    slopesKm,
    openDate,
    closeDate,
  } = snowInfoHeader
  const snowSeason = getSnowSeason(openDate, closeDate)
  const hasAltitude = isFiniteNumber(minAltitudeMeters) && isFiniteNumber(maxAltitudeMeters)
  const hasSlopes = isFiniteNumber(slopesKm)
  const resortIsOpen = snowSeason && isResortOpen(openDate, closeDate, new Date())

  return (
    <section className={styles.hero} aria-labelledby={titleId}>
      {imageUrl && (
        <Image
          alt=""
          className={styles.image}
          src={buildImgSrc(imageUrl, HERO_IMAGE_WIDTH, HERO_IMAGE_HEIGHT, ImageOrigin.CMSSitecore)}
          srcSet={buildImgSrcSet(imageUrl, HERO_IMAGE_WIDTHS, ImageOrigin.CMSSitecore)}
        />
      )}
      <div className={styles.content}>
        {snowSeason && (
          <Tag variant={resortIsOpen ? 'success-tinted' : 'neutral-tinted'}>
            <span className={styles.statusDot} aria-hidden="true" />
            {intl.formatMessage({
              id: resortIsOpen
                ? 'content_snow_info_header_resort_open'
                : 'content_snow_info_header_resort_closed',
            })}
          </Tag>
        )}
        <div className={styles.heading}>
          <Typography as="h1" id={titleId} variant="display-e-regular">
            {intl.formatMessage({ id: 'content_snow_info_header_title' }, { destinationName })}
          </Typography>
          {hasAltitude && (
            <Typography as="p" variant="body-m-regular-tight">
              {intl.formatMessage(
                { id: 'content_snow_info_header_altitude' },
                {
                  minAltitude: intl.formatNumber(minAltitudeMeters),
                  maxAltitude: intl.formatNumber(maxAltitudeMeters),
                },
              )}
            </Typography>
          )}
        </div>
        {(hasSlopes || snowSeason) && (
          <div className={styles.tokens}>
            {hasSlopes && (
              <Tag variant="neutral-tinted" icon="MountainsSlope">
                {intl.formatMessage(
                  { id: 'content_snow_info_header_slopes' },
                  { slopesKm: intl.formatNumber(slopesKm) },
                )}
              </Tag>
            )}
            {snowSeason && (
              <Tag variant="neutral-tinted" icon="Calendar">
                {intl.formatMessage(
                  { id: 'content_snow_info_header_best_time' },
                  {
                    startMonth: intl.formatDate(snowSeason.openDate, { month: 'short' }),
                    endMonth: intl.formatDate(snowSeason.closeDate, { month: 'short' }),
                  },
                )}
              </Tag>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

function isFiniteNumber(value: number | null): value is number {
  return Number.isFinite(value)
}
